import React, { useState, useEffect } from 'react';
import FormSearchAndSelectInput from 'common/FormSearchAndSelectInput';
import { useAuth } from 'context/AuthContext';

const PatientNameInput = ({ formData, setFormData, handleChange: parentHandleChange, disabled = false, required = true }) => {
    const { token } = useAuth();
    const [patientOptions, setPatientOptions] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [patientsData, setPatientsData] = useState([]); // Store full patient objects

    // Update patient options when typing
    useEffect(() => {
        if (disabled) return;
        
        const searchPatients = async () => {
            if (searchQuery.trim().length < 2) {
                setPatientOptions([]);
                setPatientsData([]);
                return;
            }

            try {
                // Validate access token
                if (!token) {
                    console.error('No authentication token found');
                    setPatientOptions([]);
                    setPatientsData([]);
                    return;
                }

                // Search query - backend will handle searching by first_name, last_name, and email
                // Fetch patients from backend API
                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ search: searchQuery.trim(), limit: 10 })
                    }
                );

                const result = await response.json();

                if (!response.ok || result.error) {
                    const errorMessage = result.error || 'Failed to fetch patients';
                    console.error('Error searching patients:', errorMessage);
                    setPatientOptions([]);
                    setPatientsData([]);
                    return;
                }

                // Backend returns data in result.data (should be an array)
                const data = Array.isArray(result.data) ? result.data : [];

                if (data && data.length > 0) {
                    // Store full patient data for later lookup
                    setPatientsData(data);
                    
                    // Format as display strings with email in parentheses
                    const formattedOptions = data.map(patient => {
                        const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
                        const displayName = patient.email ? `${fullName} (${patient.email})` : fullName;
                        return displayName;
                    });
                    setPatientOptions(formattedOptions);
                } else {
                    setPatientOptions([]);
                    setPatientsData([]);
                }
            } catch (error) {
                console.error('Error searching patients:', error);
                setPatientOptions([]);
                setPatientsData([]);
            }
        };

        // Debounce the search to avoid too many API calls
        const timeoutId = setTimeout(searchPatients, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery, disabled]);

    const handleChange = (e, value) => {
        // Update search query for Autocomplete
        setSearchQuery(value);
        
        // Always update patientName to track changes for the button text
        let updatedFormData = { ...formData };
        
        // Check if this is a known patient (contains email in parentheses)
        if (value && value.includes('(') && value.includes(')')) {
            const emailMatch = value.match(/\(([^)]+)\)/);
            const email = emailMatch ? emailMatch[1] : null;
            
            // Try to find the patient in our stored data to get their ID
            if (email) {
                const patient = patientsData.find(p => p.email === email);
                const namePart = value.split(' (')[0].trim();
                
                updatedFormData = { 
                    ...updatedFormData,
                    patientName: namePart,
                    patient_id: patient?.id || null,
                    sendInvoiceByInfo: formData.sendInvoiceBy === 'email' ? (email || formData.sendInvoiceByInfo || '') : formData.sendInvoiceByInfo,
                    patientEmail: email || formData.patientEmail || ''
                };
            } else {
                updatedFormData = { ...updatedFormData, patientName: value, patient_id: null };
            }
        } else {
            // Free text input - no patient selected from dropdown
            updatedFormData = { ...updatedFormData, patientName: value, patient_id: null };
        }
        
        setFormData(updatedFormData);
        
        // Also call parent handleChange to trigger edit tracking for pending invoices
        if (parentHandleChange) {
            // Create a synthetic event object to match the expected format
            const syntheticEvent = {
                target: {
                    name: 'patientName',
                    value: updatedFormData.patientName
                }
            };
            parentHandleChange(syntheticEvent);
        }
    };

    // Update search query when formData.patientName changes (e.g., when editing existing invoice)
    useEffect(() => {
        if (formData.patientName && formData.patientName !== searchQuery) {
            setSearchQuery(formData.patientName);
        }
    }, [formData.patientName]);

    return (
        <FormSearchAndSelectInput
            label="Patient's Name"
            name="patientName"
            onChange={handleChange}
            required={required}
            options={patientOptions}
            value={formData.patientName || ''}
            disabled={disabled}
            placeholder="Search or enter patient name..."
            labelSize={6}
            inputSize={6}
            style={{ marginBottom: '8px' }}
        />
    );
};

export default PatientNameInput;